// Emoji data (emojibase, with GitHub/Discord-style shortcodes like :fire: and :thumbsup:), loaded on first use so
// the app starts without it. Search powers the :autocomplete: in the composer and the picker.
import { useMemo, useSyncExternalStore } from "react";

export interface Emoji {
  unicode: string;
  label: string;
  /** Shortcodes without colons, e.g. ["thumbsup", "+1"]. */
  shortcodes: string[];
  tags: string[];
  group: number;
  order: number;
}

export const GROUPS: { id: number; label: string; icon: string }[] = [
  { id: 0, label: "Smileys", icon: "😀" },
  { id: 1, label: "People", icon: "👋" },
  { id: 3, label: "Nature", icon: "🐻" },
  { id: 4, label: "Food", icon: "🍕" },
  { id: 5, label: "Travel", icon: "✈️" },
  { id: 6, label: "Activities", icon: "⚽" },
  { id: 7, label: "Objects", icon: "💡" },
  { id: 8, label: "Symbols", icon: "❤️" },
  { id: 9, label: "Flags", icon: "🏳️" },
];

let loading: Promise<Emoji[]> | null = null;
let loaded: Emoji[] | null = null;

/**
 * One emoji from each Unicode emoji version that older devices may lack, oldest first. A device that can't draw an
 * emoji shows a box or a striped "missing" glyph instead, so the picker and :autocomplete: leave out every emoji newer
 * than the newest version this device draws (like Discord).
 */
const SAMPLES: [number, string][] = [
  [11, "🥰"], [12, "🥱"], [13, "🥲"], [13.1, "😶‍🌫️"], [14, "🫠"], [15, "🫨"], [15.1, "🙂‍↔️"], [16, "🫩"], [17, "🫪"], [18, "🫫"],
];

/** True when this browser draws `emoji` as one colour glyph (not a box, a grey "missing" glyph, or pieces of a sequence). */
function draws(ctx: CanvasRenderingContext2D, emoji: string, oneWidth: number): boolean {
  const size = 32;
  ctx.clearRect(0, 0, size * 3, size * 1.5);
  // A sequence the font doesn't know falls apart into several glyphs, side by side.
  if (ctx.measureText(emoji).width > oneWidth * 1.4) return false;
  ctx.fillText(emoji, 0, 0);
  const px = ctx.getImageData(0, 0, size * 1.5, size * 1.5).data;
  for (let i = 0; i < px.length; i += 4) {
    const [r, g, b, a] = [px[i]!, px[i + 1]!, px[i + 2]!, px[i + 3]!];
    // Colour emoji have coloured pixels; boxes and "missing" glyphs are drawn in grey.
    if (a > 64 && (Math.abs(r - g) > 24 || Math.abs(g - b) > 24 || Math.abs(r - b) > 24)) return true;
  }
  return false;
}

let newest: number | null = null;

/** The newest emoji version this device draws (Infinity if it can't be measured: then nothing is left out). */
export function newestEmojiVersion(): number {
  if (newest !== null) return newest;
  newest = Infinity;
  try {
    const canvas = document.createElement("canvas");
    canvas.width = 96;
    canvas.height = 48;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return newest;
    ctx.font = '32px "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif';
    ctx.textBaseline = "top";
    const oneWidth = ctx.measureText("😀").width;
    // If even a basic emoji doesn't draw in colour, the test can't tell anything (e.g. canvas reads are blocked).
    if (!draws(ctx, "😀", oneWidth)) return newest;
    let ok = 5;
    for (const [version, emoji] of SAMPLES) {
      if (!draws(ctx, emoji, oneWidth)) break;
      ok = version;
    }
    if (ok < SAMPLES.at(-1)![0]) newest = ok;
  } catch {
    // Leave everything in.
  }
  return newest;
}

export function loadEmoji(): Promise<Emoji[]> {
  loading ??= (async () => {
    const [{ default: data }, { default: codes }] = await Promise.all([
      import("emojibase-data/en/data.json"),
      import("emojibase-data/en/shortcodes/github.json"),
    ]);
    const max = newestEmojiVersion();
    const list: Emoji[] = [];
    for (const e of data as { emoji: string; label: string; hexcode: string; tags?: string[]; group?: number; order?: number; version: number }[]) {
      if (e.group === undefined || e.group === 2) continue; // skip skin-tone swatches and lone regional letters
      if (e.version > max) continue; // this device can't draw it
      const sc = (codes as Record<string, string | string[]>)[e.hexcode];
      list.push({
        unicode: e.emoji,
        label: e.label,
        // Word-like codes first (:thumbsup: before :+1:), since the first one is what we show.
        shortcodes: sc ? (Array.isArray(sc) ? sc : [sc]).sort((a, b) => Number(/^[a-z]/.test(b)) - Number(/^[a-z]/.test(a))) : [],
        tags: e.tags ?? [],
        group: e.group,
        order: e.order ?? 0,
      });
    }
    list.sort((a, b) => a.order - b.order);
    loaded = list;
    return list;
  })();
  return loading;
}

export const emojiReady = () => loaded;

/**
 * Loads the emoji data once the page has loaded and the browser is idle (never during cold start), so the first
 * reaction doesn't wait for the download. The chunk is cached with the app's other assets after that.
 */
export function preloadEmoji() {
  const later = () => {
    const idle = (window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => void }).requestIdleCallback;
    if (idle) idle(() => void loadEmoji().catch(() => (loading = null)), { timeout: 8000 });
    else setTimeout(() => void loadEmoji().catch(() => (loading = null)), 3000);
  };
  if (document.readyState === "complete") setTimeout(later, 2000);
  else addEventListener("load", () => setTimeout(later, 2000), { once: true });
}

/** Best matches for a query typed after ":" (shortcode prefix first, then shortcode/label/tag contains). */
export function searchEmoji(list: Emoji[], query: string, limit = 8): Emoji[] {
  const q = query.toLowerCase().replace(/^:|:$/g, "");
  if (!q) return [];
  const freq = readFrequent();
  const scored: [number, Emoji][] = [];
  for (const e of list) {
    let score = 0;
    for (const sc of e.shortcodes) {
      if (sc === q) score = Math.max(score, 100);
      else if (sc.startsWith(q)) score = Math.max(score, 80 - sc.length / 10);
      else if (sc.includes(q)) score = Math.max(score, 50);
    }
    if (!score && e.label.toLowerCase().startsWith(q)) score = 40;
    if (!score && e.label.toLowerCase().includes(q)) score = 30;
    if (!score && e.tags.some((t) => t.startsWith(q))) score = 20;
    if (score) scored.push([score + (freq[e.unicode] ? 5 : 0), e]);
  }
  return scored
    .sort((a, b) => b[0] - a[0] || a[1].order - b[1].order)
    .slice(0, limit)
    .map(([, e]) => e);
}

/** Exact :shortcode: → emoji, for turning a finished ":fire:" into 🔥. */
export function byShortcode(list: Emoji[], code: string): Emoji | undefined {
  const c = code.toLowerCase();
  return list.find((e) => e.shortcodes.includes(c));
}

// ---- Frequently and recently used (per device) ----
// Keys are what a reaction or message carries: a Unicode emoji, or a server emoji's token "<:name:id>". A server emoji
// is one emoji whatever it's called, so its uses are tracked by id (a rename keeps its history). Callers say which
// keys they can show (`allow`): a server emoji only counts in the server it belongs to, and only while it exists.

const FREQ_KEY = "bloom:emoji-frequent";
const DEFAULT_FREQUENT = ["👍", "❤️", "😂", "🔥", "🎉", "😮", "😢", "🙏"];

/** Unicode only: for callers that can't draw server emoji. */
const unicodeOnly = (u: string) => !u.startsWith("<");
/** One identity per emoji: a server emoji by its id, whatever name its token carries. */
const same = (u: string) => {
  const id = u.startsWith("<") ? /:([0-9A-Za-z]+)>$/.exec(u)?.[1] : undefined;
  return id ? `c:${id}` : u;
};

function readFrequent(): Record<string, number> {
  try {
    return JSON.parse(localStorage.getItem(FREQ_KEY) ?? "{}");
  } catch {
    return {};
  }
}

/** Counts a use (a reaction added, an emoji picked or typed) toward frequent and recent. */
export function noteEmojiUse(key: string) {
  const id = same(key);
  const f = readFrequent();
  // Fold any older tokens for the same server emoji (it may have been renamed) into this one.
  let n = 0;
  for (const k of Object.keys(f)) {
    if (same(k) !== id) continue;
    n += f[k] ?? 0;
    delete f[k];
  }
  f[key] = n + 1;
  const recent = [key, ...readRecent().filter((u) => same(u) !== id)].slice(0, RECENT_MAX);
  memRecent = recent;
  try {
    localStorage.setItem(FREQ_KEY, JSON.stringify(f));
    localStorage.setItem(RECENT_KEY, JSON.stringify(recent));
  } catch {
    // private mode: remembered for this visit only (memRecent)
  }
  recentVersion++;
  for (const l of recentListeners) l();
}

const RECENT_KEY = "bloom:emoji-recent";
const RECENT_MAX = 24;
let memRecent: string[] | null = null;
let recentVersion = 0;
const recentListeners = new Set<() => void>();

function readRecent(): string[] {
  if (memRecent) return memRecent;
  try {
    const v: unknown = JSON.parse(localStorage.getItem(RECENT_KEY) ?? "[]");
    memRecent = Array.isArray(v) ? v.filter((u): u is string => typeof u === "string") : [];
  } catch {
    memRecent = [];
  }
  return memRecent;
}

/** The last n emoji used here, newest first; topped up from the frequent ones (then the defaults) until there's history. */
export function recentEmoji(n = 3, allow: (u: string) => boolean = unicodeOnly): string[] {
  return uniq([...readRecent().filter(allow), ...frequentEmoji(n + DEFAULT_FREQUENT.length, allow)]).slice(0, n);
}

/** The most used emoji here, most first, then the defaults. */
export function frequentEmoji(n = 16, allow: (u: string) => boolean = unicodeOnly): string[] {
  const f = readFrequent();
  const ranked = Object.entries(f)
    .sort((a, b) => b[1] - a[1])
    .map(([u]) => u)
    .filter(allow);
  return uniq([...ranked, ...DEFAULT_FREQUENT]).slice(0, n);
}

/** Keeps the first of each emoji (by identity, so two tokens for one server emoji count once). */
function uniq(list: string[]): string[] {
  const seen = new Set<string>();
  return list.filter((u) => {
    const k = same(u);
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

/**
 * Recent or frequent emoji, kept current: re-drawn whenever an emoji is used (here or in another tab). `allow` and
 * `key` come together: change `key` when what `allow` lets through changes (another server, its emoji list).
 */
export function useUsedEmoji(kind: "recent" | "frequent", n: number, allow: (u: string) => boolean = unicodeOnly, key: unknown = ""): string[] {
  const v = useSyncExternalStore(subscribeRecent, () => recentVersion);
  return useMemo(() => (kind === "recent" ? recentEmoji(n, allow) : frequentEmoji(n, allow)), [v, kind, n, key]);
}

function subscribeRecent(l: () => void) {
  const onStorage = (e: StorageEvent) => {
    if (e.key !== RECENT_KEY && e.key !== FREQ_KEY) return;
    memRecent = null;
    recentVersion++;
    l();
  };
  recentListeners.add(l);
  window.addEventListener("storage", onStorage);
  return () => {
    recentListeners.delete(l);
    window.removeEventListener("storage", onStorage);
  };
}
